import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Coins, ArrowUp, ArrowDown } from "@phosphor-icons/react";

export default function MetalLedger() {
  const [metals, setMetals] = useState([]);
  const [metalName, setMetalName] = useState("Gold");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/metals").then((r) => setMetals(r.data)); }, []);
  useEffect(() => {
    if (!metalName) return;
    api.get(`/metal-ledger?metal_name=${encodeURIComponent(metalName)}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [metalName]);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="metal-ledger-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Stock Accounting</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">Metal Ledger</h1>
          <p className="text-muted-foreground text-sm mt-1">Pure-weight movement across purchases, sales, exchanges and manufacturing.</p>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Metal</Label>
          <Select value={metalName} onValueChange={setMetalName}>
            <SelectTrigger data-testid="metal-ledger-select" className="mt-1.5 bg-card border-border w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{metals.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </header>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Opening Stock" value={`${data.opening_stock.toFixed(3)} g`} icon={Coins} />
            <Stat label="Purchased" value={`+${data.total_purchased.toFixed(3)} g`} icon={ArrowDown} good />
            <Stat label="Old Gold In" value={`+${data.total_old_gold_in.toFixed(3)} g`} icon={ArrowDown} good />
            <Stat label="Received Back" value={`+${data.total_received_back.toFixed(3)} g`} icon={ArrowDown} good />
            <Stat label="Sold" value={`-${data.total_sold.toFixed(3)} g`} icon={ArrowUp} bad />
            <Stat label="Issued to Karigar" value={`-${data.total_issued_to_karigar.toFixed(3)} g`} icon={ArrowUp} bad />
            <div className="rounded-lg border border-gold/40 bg-gold/5 p-4 md:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Closing Stock</div>
              <div className="font-display text-3xl mt-1 tabular-nums text-gold">{data.closing_stock.toFixed(3)} g</div>
            </div>
          </div>

          <div className="rounded-lg border border-border overflow-hidden">
            <div className="px-4 py-2 bg-secondary/40 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Movement Detail</div>
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/20 hover:bg-secondary/20">
                  <TableHead>Date</TableHead><TableHead>Ref</TableHead><TableHead>Type</TableHead>
                  <TableHead>Narration</TableHead>
                  <TableHead className="text-right">In (g)</TableHead><TableHead className="text-right">Out (g)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.entries.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No movement yet.</TableCell></TableRow> :
                  data.entries.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{e.date}</TableCell>
                      <TableCell className="font-mono text-xs">{e.ref}</TableCell>
                      <TableCell><span className="text-xs uppercase tracking-wider px-2 py-1 rounded bg-secondary border border-border">{e.type}</span></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{e.narration}</TableCell>
                      <TableCell className="text-right tabular-nums text-green-400">{e.in > 0 ? e.in.toFixed(3) : ""}</TableCell>
                      <TableCell className="text-right tabular-nums text-rose-400">{e.out > 0 ? e.out.toFixed(3) : ""}</TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, good, bad }) => (
  <div className="kpi-card rounded-xl p-4 flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
      <div className={`h-8 w-8 grid place-items-center rounded-lg ${good ? "bg-green-500/15 text-green-400" : bad ? "bg-rose-500/15 text-rose-400" : "bg-primary/10 text-gold border border-primary/30"}`}>
        <Icon size={14} weight="duotone" />
      </div>
    </div>
    <div className="font-display text-lg tabular-nums">{value}</div>
  </div>
);
